import React, { useState, useCallback, useEffect } from 'react';
import {
  Tabs,
  Tab,
  Container,
  Stack,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
import Iconify from 'src/components/iconify';

import { getCandidateDetails } from 'src/services/candidate.service';
import { useNavigate, useParams } from 'react-router';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import { LoadingScreen } from 'src/components/loading-screen';
import { useSettingsContext } from 'src/components/settings';
import { Profile } from '../tabs';
import { useQuery } from 'src/hooks/use-query';
import { useCreateSearchParams } from 'src/hooks/use-create-search-params';

const CANDIDATE_DETAILS_TABS = [{ value: 'profile', label: 'Profile' }];

const CandidateDetailsView = ({ role_id }) => {
  // states
  const [currentTab, setCurrentTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [candidateChanged, setCandidateChanged] = useState(false);
  const [candidateError, setCandidateError] = useState(null);

  // hooks
  const settings = useSettingsContext();
  const params = useParams();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const organization_id = getOrganizationId(user);

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const renderTabs = (
    <Tabs
      value={currentTab}
      onChange={handleChangeTab}
      sx={{
        mb: { xs: 3, md: 5 },
      }}
    >
      {CANDIDATE_DETAILS_TABS.map(tab => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
        />
      ))}
    </Tabs>
  );

  const searchQuery = useQuery();

  const tablePage = searchQuery.get('tablePage');
  const searchParams = useCreateSearchParams({
    tablePage,
  });

  const handleBackButton = useCallback(() => {
    if (tablePage) {
      navigate(`${paths.dashboard.candidates.root}?${searchParams}`);
    } else {
      navigate(-1);
    }
  }, [navigate, searchParams, tablePage]);

  useEffect(() => {
    const caller = async () => {
      const res = await getCandidateDetails(
        organization_id,
        params.candidateId,
      );
      console.log(res);
      if (res.success) {
        setLoading(false);
        setCandidateDetails(res.body);
      } else {
        setLoading(false);
        setCandidateError('Error getting candidate details');
      }
    };
    caller();
  }, [candidateChanged]);

  return (
    <>
      {loading && <LoadingScreen loadingText="Loading..." />}
      {candidateError && (
        <Container maxWidth={settings.themeStretch ? false : 'lg'}>
          <Stack
            spacing={1.5}
            direction="row"
            alignItems="center"
            sx={{
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ mb: '1rem' }}>
                <Typography variant="h4">Candidate Details</Typography>
              </Box>
              <Box>
                <Button
                  onClick={handleBackButton}
                  startIcon={
                    <Iconify icon="eva:arrow-ios-back-fill" width={16} />
                  }
                >
                  Back
                </Button>
              </Box>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1.5}></Stack>
          </Stack>
          <Typography variant="h6">{candidateError}</Typography>
        </Container>
      )}
      {!loading && candidateDetails && (
        <Container maxWidth={settings.themeStretch ? false : 'lg'}>
          <Stack
            spacing={1.5}
            direction="row"
            alignItems="center"
            sx={{
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ mb: '1rem' }}>
                <Typography variant="h4">{candidateDetails.name}</Typography>
              </Box>
              <Box>
                <Button
                  onClick={handleBackButton}
                  startIcon={
                    <Iconify icon="eva:arrow-ios-back-fill" width={16} />
                  }
                >
                  Back
                </Button>
              </Box>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1.5}></Stack>
          </Stack>

          {renderTabs}

          {currentTab === 'profile' && (
            <Profile
              setCandidateChanged={setCandidateChanged}
              candidateDetails={candidateDetails}
            />
          )}
        </Container>
      )}
    </>
  );
};

CandidateDetailsView.propTypes = {};

export default CandidateDetailsView;
